import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './SalesInsight.module.css';
import {
  useHighlights,
  useProductDetail,
  useProductRanking,
  useProductTrend,
  useSearchTrend,
} from '../../hooks/ranking/useInsightRanking';
import {
  chartPoints,
  filterRanks,
  periodLabel,
  shortDate,
  stockType,
  trendLabel,
  trendTone as toTrendTone,
} from './insightMapping';

// 주간 랭킹 TOP 5 카드 — scope 있는 카드만 GET /insight/product-ranking.
// 질병별·연령성별·외국인은 기획 제외(고객 정보 없음) → 빈 리스트
const rankCards = [
  ['전국 TOP', 'national'],
  ['우리 지역 TOP', 'region'],
  ['약국취급률 TOP', 'pharmacy_rate'],
];
const rankScopes = Object.fromEntries(rankCards);


function DetailChart({ points, tone = 'gray' }) {
  const baseline = tone === 'up' || tone === 'down' ? 112 : 94;
  return (
    <svg
      className={
        tone === 'up'
          ? styles.detailChartGreen
          : tone === 'down'
            ? styles.detailChartRed
            : styles.detailChart
      }
      viewBox="0 0 400 131"
      preserveAspectRatio="none"
    >
      {points && (
        <>
          <polygon points={`${points} 388,${baseline} 12,${baseline}`} />
          <polyline points={points} />
        </>
      )}
    </svg>
  );
}

function ChartDates({ dates }) {
  return (
    <small>
      {dates.map((date, i) => <span key={i}>{shortDate(date)}</span>)}
    </small>
  );
}

// 로딩 자리표시 (10/15 요청 — 데이터가 갑자기 튀어나오지 않게)
function Sk({ w = '100%', h = 14 }) {
  return <span className={styles.skel} style={{ width: w, height: h }} />;
}

// 재고 아이콘 — in 재고 / none 미취급 (Figma 15:3746, 백엔드 my_stock_status 10/23)
function Stock({ type }) {
  if (type === 'none')
    return <img className={styles.notStock} src="/images/insight/stock-not.svg" alt="미취급" />;
  if (type === 'in') return <span className={styles.stock}><i /></span>;
  return null;
}

function TrendValue({ value }) {
  if (value === '—') return <i className={styles.stall} />;
  const type = value.startsWith('🔥')
    ? 'fire'
    : value.startsWith('▼')
      ? 'down'
      : 'up';
  return (
    <>
      <img
        src={`/images/insight/trend-${type}.svg`}
        alt={type === 'fire' ? '급상승' : type === 'down' ? '하락' : '상승'}
      />
      {value.replace(/^[▲▼🔥]\s*/u, '')}
    </>
  );
}

// 미취급·결품 칩은 재고 데이터가 없어 제거 (10/15 결정)
// 결품 칩은 판정 불가로 제외 (백엔드 10/23)
const nationalFilters = ['전체', '미취급', '급상승 (▲5+)', '하락'];

const detailFilters = {
  '전국 TOP': nationalFilters,
  '우리 지역 TOP': ['전체', ['지역 약국', '-'], '반경 5km'],
  '약국취급률 TOP': ['전체', '취급률 ▲'],
};

// 값은 랭킹 응답 context[{label,value}]. 선택지 목록 API(D-5) 없음 → 현재 값만
const contextFilters = {
  '우리 지역 TOP': ['지역'],
};

// Options 컴포넌트 (Figma 76:18458) — 네이티브 select를 투명하게 덮어 메뉴는 OS 기본
function ContextSelect({ label, value }) {
  return (
    <label className={styles.selectFilter}>
      <small>{label}</small>
      <b>{value ?? '-'}</b>
      <svg className={styles.selectChevron} viewBox="0 0 18 18" aria-hidden="true">
        <path d="M9 12.75C8.80853 12.749 8.61968 12.7055 8.44715 12.6224C8.27461 12.5394 8.12272 12.419 8.0025 12.27L4.845 8.445C4.6605 8.21473 4.54439 7.93725 4.50991 7.6442C4.47544 7.35115 4.52398 7.05431 4.65 6.7875C4.75221 6.55562 4.919 6.35806 5.13046 6.21842C5.34192 6.07877 5.58911 6.00295 5.8425 6H12.1575C12.4109 6.00295 12.6581 6.07877 12.8695 6.21842C13.081 6.35806 13.2478 6.55562 13.35 6.7875C13.476 7.05431 13.5246 7.35115 13.4901 7.6442C13.4556 7.93725 13.3395 8.21473 13.155 8.445L9.9975 12.27C9.87728 12.419 9.72539 12.5394 9.55285 12.6224C9.38032 12.7055 9.19147 12.749 9 12.75Z" fill="#55575D" />
      </svg>
      <select aria-label={label} value={value ?? ''} onChange={() => {}}>
        {value != null && <option>{value}</option>}
      </select>
    </label>
  );
}

const insightViews = {
  national: '전국 TOP',
  region: '우리 지역 TOP',
  pharmacy: '약국취급률 TOP',
};

const insightPaths = Object.fromEntries(
  Object.entries(insightViews).map(([path, title]) => [title, path])
);

function Detail({ category }) {
  const scope = rankScopes[category];
  const { data: ranking, isLoading: rankingLoading } = useProductRanking({ scope, limit: 30 });
  const [activeRow, setActiveRow] = useState(null);
  const [filter, setFilter] = useState('전체');
  const [expanded, setExpanded] = useState(false);
  const items = ranking?.items ?? [];
  const selected = items.find(item => item.product_id === activeRow) ?? null;
  const { data: productTrend, isLoading: rankTrendLoading } = useProductTrend(selected?.product_id, scope);
  const { data: productDetail, isLoading: detailLoading } = useProductDetail(selected?.product_id);
  const healthInfo = productDetail?.available ? productDetail.health_info : null;
  const { data: searchTrend } = useSearchTrend(selected?.barcode);
  const trendTone = toTrendTone(selected);
  const compactStock = category !== '전국 TOP';
  const filtered =
    category === '전국 TOP' ? filterRanks(items, filter) : items;
  const visibleRanks = expanded ? filtered : filtered.slice(0, 12);
  const rankSeries = productTrend?.series ?? [];
  const searchSeries = searchTrend?.series ?? [];
  const deltaPct = searchTrend?.delta_pct;
  return (
    <div className={styles.detail}>
      <section className={styles.rankList}>
        <div className={styles.filters}>
          {detailFilters[category] && (
            <div className={styles.filterGroup}>
              {detailFilters[category].map(chip => {
                const [label, count] = [].concat(chip);
                return (
                  <button
                    className={filter === label ? styles.filterOn : ''}
                    onClick={() => {
                      setFilter(label);
                      setActiveRow(null);
                      setExpanded(false);
                    }}
                    key={label}
                  >
                    {label}
                    {count && <small>{count}</small>}
                  </button>
                );
              })}
            </div>
          )}
          {contextFilters[category] && (
            <div className={styles.contextFilters}>
              {contextFilters[category].map(label => (
                <ContextSelect
                  key={label}
                  label={label}
                  value={ranking?.context?.find(c => c.label === label)?.value}
                />
              ))}
            </div>
          )}
        </div>
        {category === '전국 TOP' ? (
          <div className={styles.legend}>
            <Stock type="in" /> 재고　 <Stock type="none" /> 미취급
          </div>
        ) : (
          <div className={styles.filterGap} />
        )}
        <div className={styles.tableHead}>
          <span>순위</span>
          <span>상품명</span>
          <span>전주대비</span>
          <span>재고</span>
        </div>
        {rankingLoading &&
          Array.from({ length: 12 }, (_, i) => (
            <button disabled key={`sk${i}`}>
              <Sk w={14} />
              <Sk w="60%" />
              <Sk w={28} />
              <span />
            </button>
          ))}
        {visibleRanks.map(item => (
          <button
            className={activeRow === item.product_id ? styles.rowOn : ''}
            onClick={() => setActiveRow(item.product_id)}
            key={item.product_id}
          >
            <b>{item.rank}</b>
            <span>{item.name}</span>
            <em className={item.trend === 'down' ? styles.blue : ''}>
              <TrendValue value={trendLabel(item)} />
            </em>
            <Stock type={stockType(item)} />
          </button>
        ))}
        {filtered.length > 12 && <button
          className={styles.more}
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? 'TOP 12만 보기' : '13–30위 더 보기'}
          <img
            className={`${styles.moreChevron} ${expanded ? styles.chevronUp : ''}`}
            src="/images/insight/chevron-down.svg"
            alt=""
          />
        </button>}
      </section>
      <aside className={styles.detailPanel}>
        {!selected ? (
          <div className={styles.emptyDetail}>
            <img src="/images/insight/empty-box.svg" alt="" />
            <strong>상품을 선택하세요</strong>
            <p>
              왼쪽 목록에서 상품을 선택하면
              <br />
              재고와 발주 추천을 확인할 수 있습니다
            </p>
          </div>
        ) : (
          <div>
        <div className={styles.productHero}>
          <span>
            전국 {selected.rank}위
            <em className={trendTone === 'down' ? styles.heroTrendDown : ''}>
              <TrendValue value={trendLabel(selected)} /> (전주 대비)
            </em>
          </span>
          <h1>{selected.name}</h1>
          <small>{selected.manufacturer ?? '-'}　·　{selected.category ?? '-'}</small>
        </div>
        {searchTrend?.available && (
          <section>
            <h2>{searchTrend.category?.name ?? '-'} · 카테고리 검색 트렌드</h2>
            <div className={styles.platforms}>
              <div>
                <span>네이버 데이터랩</span>
                <em>
                  <TrendValue
                    value={
                      deltaPct == null
                        ? '—'
                        : `${deltaPct < 0 ? '▼' : '▲'}${Math.abs(deltaPct)}%`
                    }
                  />
                </em>
                <b>{searchTrend.score ?? '-'} /100</b>
                <small>{searchTrend.score_basis ?? '-'}</small>
              </div>
            </div>
            <div className={styles.chart}>
              <span>8주 검색 추이 (카테고리 합산)</span>
              <em>{periodLabel(searchTrend.period?.start, searchTrend.period?.end)}</em>
              <DetailChart points={chartPoints(searchSeries.map(s => s.score))} />
              <ChartDates dates={searchSeries.map(s => s.week_start)} />
              <div className={styles.keywords}>
                <span>합산 키워드</span>
                <p>
                  {(searchTrend.keywords ?? []).map(keyword => (
                    <b key={keyword}>{keyword}</b>
                  ))}
                </p>
              </div>
            </div>
          </section>
        )}
        {stockType(selected) && (
          <section>
            <h2>내 약국 재고</h2>
            {/* 재고 수량은 없음 → 최근 30일 판매·마지막 판매로 대체 (백엔드 10/23) */}
            <div className={`${styles.stockCard} ${styles.stockCardCompact} ${stockType(selected) === 'none' ? styles.stockNone : ''}`}>
              <strong>
                {stockType(selected) === 'in' ? (
                  <><img className={styles.statusIcon} src="/images/insight/stock-check.svg" alt="" />보유</>
                ) : (
                  <><Stock type="none" /> 미취급</>
                )}
              </strong>
              <b>
                {stockType(selected) === 'in'
                  ? [`최근 30일 ${Number(selected.my_sales_qty_30d || 0).toLocaleString()}개 판매`, selected.my_last_sold_at && `마지막 판매 ${selected.my_last_sold_at}`].filter(Boolean).join(' · ')
                  : '최근 60일 판매 없음'}
              </b>
            </div>
          </section>
        )}
        <section className={styles.rankTrend}>
          <div className={styles.rankTrendHead}>
            <h2>전국 · 지난 8주 순위 추이</h2>
            <span>
              {periodLabel(rankSeries[0]?.date, rankSeries.at(-1)?.date)}
            </span>
          </div>
          <div
            className={`${styles.chart} ${styles.rankTrendChart} ${compactStock ? styles.rankTrendCompact : ''}`}
          >
            {rankTrendLoading ? (
              <Sk h={131} />
            ) : (
              <>
                <DetailChart
                  tone={trendTone}
                  points={chartPoints(rankSeries.map(s => s.national_rank), true)}
                />
                <ChartDates dates={rankSeries.map(s => s.date)} />
              </>
            )}
          </div>
        </section>
        {/* 함께 구매 TOP 3: POS 에 영수증(거래) 단위 데이터가 없어 산출 불가 → 블록 숨김 (10/15 결정) */}
        {/* GET /insight/product-detail health_info (dosage 는 약 35% 상품만) */}
        <section>
          <h2>건기식 정보</h2>
          <div className={styles.healthInfo}>
            {detailLoading ? <Sk w="70%" h={18} /> : <b>{healthInfo?.summary || '-'}</b>}
            {detailLoading ? <Sk w="90%" /> : <p>{healthInfo?.dosage || '-'}</p>}
            <hr />
            <span>추천 대상 환자</span>
            <div>
              {(healthInfo?.target_tags || []).map(tag => (
                <em key={tag}>{tag}</em>
              ))}
            </div>
          </div>
        </section>
          </div>
        )}
      </aside>
    </div>
  );
}

function RankCard({ title, scope }) {
  const navigate = useNavigate();
  const { data, isLoading } = useProductRanking({ scope, limit: 5 });
  return (
    <article className={styles.rankCard}>
      <h2>{title}</h2>
      <ol>
        {isLoading &&
          [1, 2, 3, 4, 5].map(no => (
            <li key={`sk${no}`}>
              <Sk w={12} />
              <Sk w="70%" />
              <Sk w={28} />
              <span />
            </li>
          ))}
        {(data?.items ?? []).map(item => (
          <li key={item.product_id}>
            <b>{item.rank}</b>
            <span>{item.name}</span>
            <em className={item.trend === 'down' ? styles.blue : styles.red}>
              <TrendValue value={trendLabel(item)} />
            </em>
            <Stock type={stockType(item)} />
          </li>
        ))}
      </ol>
      <footer>
        <button
          onClick={() =>
            navigate(`/dashboard/insight?view=${insightPaths[title]}`)
          }
        >
          전체 TOP 30 보기
          <img src="/images/insight/chevron-right.svg" alt="" />
        </button>
      </footer>
    </article>
  );
}

export default function SalesInsight() {
  const location = useLocation();
  const view = new URLSearchParams(location.search).get('view');
  const detail = insightViews[view] || null;
  const { data: highlightData, isLoading: highlightLoading } = useHighlights();
  // Figma 카드 3장: 급상승 우선, 부족하면 급하락으로 채움
  const highlights = highlightData?.available
    ? [...(highlightData.items || [])]
        .sort((a, b) => (a.type === 'surge' ? 0 : 1) - (b.type === 'surge' ? 0 : 1))
        .slice(0, 3)
    : [];
  useEffect(() => {
    const viewTitle =
      view === 'sales'
        ? '매출 현황'
        : detail === '우리 지역 TOP'
          ? '지역 TOP'
          : detail;
    window.dispatchEvent(
      new CustomEvent('insight-view', {
        detail: viewTitle || '전체 현황',
      })
    );
  }, [detail, view]);
  if (detail)
    return (
      <Detail
        key={detail}
        category={detail}
      />
    );
  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroTitle}>
          <b>이번 주 주목할 변화</b>
          <span>발주 검토</span>
        </div>
        {/* GET /insight/highlights — 3장 미만이면 남는 카드 값 '-' */}
        <div className={styles.heroCards}>
          {[0, 1, 2].map(i => {
            const item = highlights[i];
            if (highlightLoading)
              return (
                <div key={`sk${i}`}>
                  <Sk w={60} />
                  <Sk w="75%" h={20} />
                  <Sk w="55%" />
                </div>
              );
            return (
              <div key={item?.product_id ?? i}>
                <small>{item?.label || '-'}</small>
                <b>{item?.name || '-'}</b>
                {item ? (
                  <span>
                    전국 {item.national_rank}위<i>·</i>전주
                    <em>
                      <TrendValue
                        value={trendLabel({
                          trend: item.type === 'surge' ? 'surge' : 'down',
                          rank_delta: item.rank_delta,
                        })}
                      />
                    </em>
                  </span>
                ) : (
                  <span>-</span>
                )}
              </div>
            );
          })}
        </div>
      </section>
      <section>
        <div className={styles.rankHead}>
          <h1>주간 랭킹 TOP 5</h1>
          <div>
            <span><Stock type="in" /> 재고</span>
            <span><Stock type="none" /> 미취급</span>
          </div>
        </div>
        <div className={styles.grid}>
          {rankCards.map(([title, scope]) => (
            <RankCard key={title} title={title} scope={scope} />
          ))}
        </div>
      </section>
    </main>
  );
}
